import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DrillAnimation } from '../Model';
import { AnimationControls } from './AnimationControls';

interface DrillAnimatorProps {
  animation: DrillAnimation;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

const ACTOR_COLORS: Record<string, string> = {
  red: '#e74c3c',
  blue: '#3498db',
  neutral: '#f39c12',
};

const ACTOR_RADIUS = 12;
const BALL_RADIUS = 6;
const SVG_PADDING = 20;

export const DrillAnimator: React.FC<DrillAnimatorProps> = ({ animation, onRegenerate, isRegenerating }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { pitch, actors, steps } = animation;
  const step = steps[currentStep];

  // Scale pitch coordinates to SVG viewport (guard against zero/undefined dimensions)
  const svgWidth = 500;
  const pitchWidth = pitch?.width > 0 ? pitch.width : 1;
  const pitchHeight = pitch?.height > 0 ? pitch.height : 1;
  const svgHeight = (pitchHeight / pitchWidth) * svgWidth;
  const scaleX = (x: number) => SVG_PADDING + (x / pitchWidth) * (svgWidth - 2 * SVG_PADDING);
  const scaleY = (y: number) => SVG_PADDING + (y / pitchHeight) * (svgHeight - 2 * SVG_PADDING);

  const stopPlayback = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const advanceStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev >= steps.length - 1) {
        stopPlayback();
        return prev;
      }
      return prev + 1;
    });
  }, [steps.length, stopPlayback]);

  useEffect(() => {
    if (isPlaying && step) {
      const durationMs = (step.durationSeconds * 1000) / speed;
      intervalRef.current = setInterval(advanceStep, durationMs);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [isPlaying, currentStep, speed, step, advanceStep]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handlePlayPause = () => {
    if (isPlaying) {
      stopPlayback();
    } else {
      if (currentStep >= steps.length - 1) {
        setCurrentStep(0);
      }
      setIsPlaying(true);
    }
  };

  const handleStepBack = () => {
    stopPlayback();
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const handleStepForward = () => {
    stopPlayback();
    setCurrentStep((prev) => Math.min(steps.length - 1, prev + 1));
  };

  if (!step) return null;

  const ballCarrier = step.ballCarrierId ? step.positions[step.ballCarrierId] : null;

  return (
    <div className="drill-animator">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="drill-animator-pitch"
        role="img"
        aria-label="Drill animation pitch view"
      >
        {/* Pitch background */}
        <rect x="0" y="0" width={svgWidth} height={svgHeight} fill="#2d8a4e" rx="4" />

        {/* Pitch outline */}
        <rect
          x={SVG_PADDING}
          y={SVG_PADDING}
          width={svgWidth - 2 * SVG_PADDING}
          height={svgHeight - 2 * SVG_PADDING}
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="2"
        />

        {/* Center line */}
        <line
          x1={svgWidth / 2}
          y1={SVG_PADDING}
          x2={svgWidth / 2}
          y2={svgHeight - SVG_PADDING}
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1"
        />

        {/* Center circle */}
        <circle
          cx={svgWidth / 2}
          cy={svgHeight / 2}
          r={Math.min(svgWidth, svgHeight) * 0.12}
          fill="none"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1"
        />

        {/* Annotations */}
        {step.annotations && step.annotations.map((annotation, i) => (
          <text
            key={i}
            x={svgWidth / 2}
            y={svgHeight - 6}
            textAnchor="middle"
            fill="rgba(255,255,255,0.8)"
            fontSize="10"
            fontStyle="italic"
          >
            {annotation}
          </text>
        ))}

        {/* Actors */}
        {actors.map((actor) => {
          const pos = step.positions[actor.id];
          if (!pos) return null;

          const cx = scaleX(pos.x);
          const cy = scaleY(pos.y);

          if (actor.type === 'ball') {
            // Draw ball at carrier position if available, otherwise at its own position
            const ballPos = ballCarrier || pos;
            const bx = scaleX(ballPos.x);
            const by = scaleY(ballPos.y);

            return (
              <circle
                key={actor.id}
                cx={bx + (ballCarrier ? ACTOR_RADIUS + 4 : 0)}
                cy={by}
                r={BALL_RADIUS}
                fill="white"
                stroke="#333"
                strokeWidth="1"
                className="drill-animator-actor"
              />
            );
          }

          const color = ACTOR_COLORS[actor.team] || ACTOR_COLORS.neutral;
          const radius = actor.type === 'coach' ? ACTOR_RADIUS + 2 : ACTOR_RADIUS;

          return (
            <g key={actor.id} className="drill-animator-actor">
              <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill={color}
                stroke="white"
                strokeWidth="2"
              />
              <text
                x={cx}
                y={cy + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize={actor.type === 'coach' ? '8' : '9'}
                fontWeight="bold"
              >
                {actor.label}
              </text>
            </g>
          );
        })}
      </svg>

      <AnimationControls
        currentStep={currentStep}
        totalSteps={steps.length}
        isPlaying={isPlaying}
        speed={speed}
        stepDescription={step.description}
        onPlayPause={handlePlayPause}
        onStepBack={handleStepBack}
        onStepForward={handleStepForward}
        onSpeedChange={setSpeed}
      />

      {onRegenerate && (
        <div className="animation-regenerate">
          <button
            onClick={onRegenerate}
            disabled={isRegenerating}
            className="btn btn-sm btn-outline-neutral"
          >
            {isRegenerating ? 'Regenerating...' : 'Regenerate Animation'}
          </button>
        </div>
      )}
    </div>
  );
};
